/**
 * RAM — MaintenanceWizard
 *
 * Branching shell that dispatches on `RamActionCode` to the matching action
 * component. Each action renders its own `.ram-wizard` surface.
 *
 * Wave 11 dialog gap-fill (2026-04-27): added dedicated dialogs for the 9
 * actions that previously fell through to Wave10ActionFallback. Fallback is
 * retained as a defensive default for any future RamActionCode added before
 * its dedicated dialog ships.
 */
import { useNavigate } from 'react-router';
import AddressChange from './actions/AddressChange';
import HoldOrStop from './actions/HoldOrStop';
import BackOnTrack from './actions/BackOnTrack';
import DormantRelease from './actions/DormantRelease';
import SignatureCard from './actions/SignatureCard';
import PoaSuspendRevoke from './actions/PoaSuspendRevoke';
import TerminationOfAgent from './actions/TerminationOfAgent';
import UtmaSuccessor from './actions/UtmaSuccessor';
import ChangeOdLimit from './actions/ChangeOdLimit';
import ChangeTitle from './actions/ChangeTitle';
import ChangeOwnership from './actions/ChangeOwnership';
import ChangeOdProtection from './actions/ChangeOdProtection';
import ChangeStatementPrefs from './actions/ChangeStatementPrefs';
import ChangeFeatures from './actions/ChangeFeatures';
import BlockUnblock from './actions/BlockUnblock';
import CloseAccount from './actions/CloseAccount';
import ReactivateDormant from './actions/ReactivateDormant';
import ChangeRateIndex from './actions/ChangeRateIndex';
import Wave10ActionFallback from './actions/Wave10ActionFallback';
import type { RamActionCode, RamTarget } from './types';
import { RAM_ACTIONS } from './types';

export interface MaintenanceWizardProps {
  action: RamActionCode;
  target: RamTarget;
  onExit: () => void;
}

export function MaintenanceWizard({
  action,
  target,
  onExit,
}: MaintenanceWizardProps): JSX.Element {
  const navigate = useNavigate();

  const backToIndex = (): void => {
    onExit();
    navigate('/ram');
  };

  switch (action) {
    case 'address-change':
      return <AddressChange target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'hold-or-stop':
      return <HoldOrStop target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'back-on-track':
      return <BackOnTrack target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'dormant-release':
      return <DormantRelease target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'signature-card':
      return <SignatureCard target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'poa-suspend-revoke':
      return <PoaSuspendRevoke target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'termination-of-agent':
      return <TerminationOfAgent target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'utma-successor':
      return <UtmaSuccessor target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'change-od-limit':
      return <ChangeOdLimit target={target} onDone={backToIndex} onCancel={backToIndex} />;
    // ── Wave 11 dialog gap-fill (2026-04-27) — replaces Wave10ActionFallback ──
    case 'change-title':
      return <ChangeTitle target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'change-ownership':
      return <ChangeOwnership target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'change-od-protection':
      return <ChangeOdProtection target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'change-statement-prefs':
      return <ChangeStatementPrefs target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'change-features':
      return <ChangeFeatures target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'block-unblock':
      return <BlockUnblock target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'close-account':
      return <CloseAccount target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'reactivate-dormant':
      return <ReactivateDormant target={target} onDone={backToIndex} onCancel={backToIndex} />;
    case 'change-rate-index':
      return <ChangeRateIndex target={target} onDone={backToIndex} onCancel={backToIndex} />;
    default: {
      // Defensive fallback for any future RamActionCode added without a
      // dedicated dialog. Real MaintAction row + audit + approval routing
      // still flow through correctly via the typed api-client method.
      const _exhaustive: never = action;
      const def = RAM_ACTIONS.find((a) => a.code === _exhaustive);
      return (
        <Wave10ActionFallback
          action={_exhaustive}
          title={def?.title ?? String(_exhaustive)}
          target={target}
          onDone={backToIndex}
          onCancel={backToIndex}
        />
      );
    }
  }
}
